import { Inject, Injectable } from '@angular/core'
import { ConfirmDialogComponent } from '../../dialog/confirm-dialog/confirm-dialog.component'
import { MatDialog } from '@angular/material/dialog'
import {fromEvent, merge, Observable, Observer, of, Subject} from 'rxjs';
import { Router } from '@angular/router'
import { NavigateService } from './navigate.service'
import { LocalStorageService } from './local-storage.service'
import { L10N_LOCALE, L10nLocale } from 'angular-l10n'
import { LbTranslationService } from '@affluences/lb-utils-front'
import {LbHttpService} from '../lb-http.service';
import {LoadingService} from './loading.service';
import {mapTo} from 'rxjs/operators';

const MAX_STACK_LENGTH = 500
const newReload = 1000 * 60 * 3

@Injectable( {
    providedIn: 'root'
} )
export class ErrorService {

    private logStack: { type: string, message: string, data: any }[] = []
    public displayErrorOnScreen: boolean = false
    private logLevel: number = 0 // 0 = ERROR, 1 = WARNING , 2 = INFO , 3 = DEBUG
    private errorVisible: boolean = false
    private logOutErrorVisible: boolean = false
    private logSource: Subject<{ type: string, message: string, data: any }[]> = new Subject<{ type: string, message: string, data: any }[]>()
    public $log: Observable<{ type: string, message: string, data: any }[]> = new Observable<{ type: string, message: string, data: any }[]>()


    private isConnected: Subject<boolean> = new Subject<boolean>()
    public $isConnected: Observable<boolean> = new Observable<boolean>()

    private unknownErrorDialog: any
    private forceLogOutDialog: any
    public showLogger: boolean = false
    private showLoggerSource: Subject<boolean> = new Subject<boolean>()
    public $showLogger: Observable<boolean> = new Observable<boolean>()

    constructor (
        @Inject( L10N_LOCALE ) public locale: L10nLocale,
        private router: Router,
        private dialog: MatDialog,
        private lbTranslationService: LbTranslationService,
        private navigateService: NavigateService,
        private localStorageService: LocalStorageService,
        private lbHttpService: LbHttpService,
        private loader: LoadingService
    ) {
        this.$log = this.logSource.asObservable()
        this.$showLogger = this.showLoggerSource.asObservable()
        this.showLogger = false

        this.logLevel = this.localStorageService.getLogLevel()

        this.$isConnected = this.isConnected.asObservable()

        this.createOnline$().subscribe(res => {
            console.log(res)
            this.isConnected.next(res)
        })
    }

    public setDisplayError ( value ) {
        this.displayErrorOnScreen = value
    }

    public setLogLevel ( value ) {
        if (value >= 0 && value <= 3) {
            this.logLevel = value
        }
    }

    public unknownError () {
        if ( !this.errorVisible ) {
            this.errorVisible = true
            this.loader.hide()

            let timeout = setTimeout( () => this.loader.hide(), 2000)

            const data: any = {
                width: '80vw',
                data: {
                    title: this.lbTranslationService.translateFromKey( 'endpoint_ticketService_popup_unknownError_title', { lang: this.locale.language } ),
                    text: this.lbTranslationService.translateFromKey( 'endpoint_ticketService_popup_unknownError_text', { lang: this.locale.language } ),
                    confirm: this.lbTranslationService.translateFromKey( 'endpoint_confirmDialog_confirm_ok', { lang: this.locale.language } )
                }
            }
            this.unknownErrorDialog = this.dialog.open( ConfirmDialogComponent, data )

            this.unknownErrorDialog.afterClosed().subscribe( async result => {
                this.errorVisible = false
                clearTimeout(timeout)
                timeout = null
                if ( result && result.res ) {
                    return window.location.reload( )
                }
            } )
        }
    }

    public reloadInterface () {
        if ( !this.logOutErrorVisible ) {
            const lastReload = this.localStorageService.getLastReloadInterface()
            if ( !lastReload || Date.now() > (lastReload + newReload) ) {
                this.localStorageService.setLastReloadInterface( Date.now() )
                return window.location.reload( )
            }
        }
    }

    public forceLogOut () {
        if ( !this.logOutErrorVisible ) {
            this.logOutErrorVisible = true
            const data: any = {
                width: '80vw',
                data: {
                    text: this.lbTranslationService.translateFromKey( 'endpoint_operatorService_endpointClosing_text', { lang: this.locale.language } ),
                    confirm: this.lbTranslationService.translateFromKey( 'endpoint_confirmDialog_confirm_ok', { lang: this.locale.language } )
                }
            }

            if ( this.errorVisible && this.unknownErrorDialog ) {
                this.unknownErrorDialog.close( null )
            }

            this.forceLogOutDialog = this.dialog.open( ConfirmDialogComponent, data )

            this.forceLogOutDialog.afterClosed().subscribe( async result => {
                this.logOutErrorVisible = false
                return this.navigateService.goToHome()
            } )
        }
    }

    public logError ( message, data?, send = true) {
        this.log( 'error', message, data )

        try {
            if (send) {
                const errorToSend = {
                    interface: 'EndpointV2',
                    queueId: this.localStorageService.getSelectedQueue().queueId,
                    placeId: this.localStorageService.getSelectedQueue().placeId,
                    applicationId: this.localStorageService.getApplicationId(),
                    operatorId: this.localStorageService.getOperatorId(),
                    log: message,
                    error: data,
                    dateSent: new Date().toISOString(),
                    userAgent: navigator.userAgent

                }

                this.lbHttpService.sendError( errorToSend ).subscribe()
            }
        }
        catch (e) {
            this.log('error', 'Error when sending error ', data)
            return of(null)
        }

    }

    public logDebug ( message, data? ) {
        this.log( 'debug', message, data )
    }

    public logInfo ( message, data? ) {
        this.log( 'info', message, data )
    }

    private log ( type, message, data ) {

        let dataString = ''
        if ( typeof (data) !== 'undefined' ) {
            if ( typeof (data) === 'object' ) {
                dataString = ' => ' + JSON.stringify( data )
            } else {
                dataString = ' => ' + data.toString()
            }
        }

        if ( type === 'debug' && this.logLevel >= 3 ) {
            console.log( '%c[DEBUG] ', 'color: #1976D2', message + dataString )
        } else if ( type === 'info' && this.logLevel >= 2 ) {
            console.log( '%c[INFO] ', 'color: #01A39D', message + dataString )
        } else if ( type === 'error' && this.logLevel >= 0 ) {
            console.log( '%c[ERROR] ', 'color: #EB5757', message + dataString )
            if ( data ) {
                console.log( data )
            }
        }

        if ( this.displayErrorOnScreen ) {
            this.logStack.push( { type, message, data } )
            if ( this.logStack.length > MAX_STACK_LENGTH ) {
                this.logStack = this.logStack.slice( (this.logStack.length - MAX_STACK_LENGTH), (this.logStack.length - 1) )
            }
            this.logSource.next( this.logStack )
        }
    }

    public showLinebertyLogger ( value ) {
        this.showLogger = value
        this.showLoggerSource.next( this.showLogger )
    }

    public cleanLogs () {
        this.logStack = []
        this.logSource.next( this.logStack )
    }

    private createOnline$() {
        return merge<boolean>(
            fromEvent(window, 'offline').pipe(mapTo(false)),
            fromEvent(window, 'online').pipe(mapTo( true)),
            new Observable((sub: Observer<boolean>) => {
                sub.next(navigator.onLine);
                sub.complete();
            }));
    }
}
